import { directSupabase } from '../lib/supabaseClient';

export type FenestrationMetric =
  | 'internal_finish'
  | 'external_finish'
  | 'material_type'
  | 'type'
  | 'glass_type'
  | 'equal_dims'
  | 'within_50_dims'
  | 'mean_window'
  | 'mean_door'
  | 'openers'
  | 'all';

export type FenestrationFilters = {
  search: string;
  metric: FenestrationMetric;
  internalFinish: string;
  externalFinish: string;
  materialType: string;
  itemType: string;
  style: string;
  glassType: string;
  profileType: string;
  beadingType: string;
  handleColor: string;
  handleStyle: string;
  hingeType: string;
  jointStyle: string;
  cillOption: string;
  hingePlacement: string;
  openDirection: string;
  source: string;
  georgianBars: '' | 'yes' | 'no';
  astragalBars: '' | 'yes' | 'no';
  trickleVents: '' | 'yes' | 'no';
  minWidthMm: string;
  maxWidthMm: string;
  minHeightMm: string;
  maxHeightMm: string;
  minOpeners: string;
  documentKind: string;
  createdFrom: string | null;
  createdTo: string | null;
};

export type FenestrationQueryItem = {
  lineId: string;
  documentId: string;
  customerId: string;
  documentRef: string;
  documentTitle: string;
  documentKind: string;
  documentCreatedAt: string;
  qty: number;
  lineName: string;
  lineDescription: string;
  imageUrl: string;
  internalFinish: string;
  externalFinish: string;
  materialType: string;
  itemType: string;
  styleName: string;
  styleCategory: string;
  glassType: string;
  profileType: string;
  beadingType: string;
  handleColor: string;
  handleStyle: string;
  hingeType: string;
  trickleVents: boolean;
  widthMm: number;
  heightMm: number;
  openerCount: number;
  isDoor: boolean;
  metaJson: Record<string, any>;
};

export type FenestrationFilterOptions = {
  internalFinishes: string[];
  externalFinishes: string[];
  materialTypes: string[];
  itemTypes: string[];
  styles: string[];
  glassTypes: string[];
  profileTypes: string[];
  beadingTypes: string[];
  handleColors: string[];
  handleStyles: string[];
  hingeTypes: string[];
  jointStyles: string[];
  cillOptions: string[];
  hingePlacements: string[];
  openDirections: string[];
  sources: string[];
};

const EMPTY_OPTIONS: FenestrationFilterOptions = {
  internalFinishes: [],
  externalFinishes: [],
  materialTypes: [],
  itemTypes: [],
  styles: [],
  glassTypes: [],
  profileTypes: [],
  beadingTypes: [],
  handleColors: [],
  handleStyles: [],
  hingeTypes: [],
  jointStyles: [],
  cillOptions: [],
  hingePlacements: [],
  openDirections: [],
  sources: []
};

const OPTIONS_CACHE_KEY = 'fenestration_filter_options_v1';
const OPTIONS_CACHE_TTL_MS = 30 * 60 * 1000;

const optionalText = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed || null;
};

const optionalNumber = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function createFenestrationRpcParams(
  filters: FenestrationFilters,
  page: number,
  pageSize: number
) {
  return {
    p_search: optionalText(filters.search),
    p_metric: filters.metric || 'all',
    p_internal_finish: optionalText(filters.internalFinish),
    p_external_finish: optionalText(filters.externalFinish),
    p_material_type: optionalText(filters.materialType),
    p_item_type: optionalText(filters.itemType),
    p_style: optionalText(filters.style),
    p_glass_type: optionalText(filters.glassType),
    p_profile_type: optionalText(filters.profileType),
    p_beading_type: optionalText(filters.beadingType),
    p_handle_color: optionalText(filters.handleColor),
    p_handle_style: optionalText(filters.handleStyle),
    p_hinge_type: optionalText(filters.hingeType),
    p_joint_style: optionalText(filters.jointStyle),
    p_cill_option: optionalText(filters.cillOption),
    p_hinge_placement: optionalText(filters.hingePlacement),
    p_open_direction: optionalText(filters.openDirection),
    p_source: optionalText(filters.source),
    p_georgian_bars: filters.georgianBars === '' ? null : filters.georgianBars === 'yes',
    p_astragal_bars: filters.astragalBars === '' ? null : filters.astragalBars === 'yes',
    p_trickle_vents: filters.trickleVents === '' ? null : filters.trickleVents === 'yes',
    p_min_width_mm: optionalNumber(filters.minWidthMm),
    p_max_width_mm: optionalNumber(filters.maxWidthMm),
    p_min_height_mm: optionalNumber(filters.minHeightMm),
    p_max_height_mm: optionalNumber(filters.maxHeightMm),
    p_min_openers: optionalNumber(filters.minOpeners),
    p_document_kind: optionalText(filters.documentKind),
    p_created_from: filters.createdFrom,
    p_created_to: filters.createdTo,
    p_offset: Math.max(0, page - 1) * pageSize,
    p_limit: pageSize
  };
}

const mapItem = (row: any): FenestrationQueryItem => ({
  lineId: String(row.line_id || ''),
  documentId: String(row.document_id || ''),
  customerId: String(row.customer_id || ''),
  documentRef: String(row.document_ref || ''),
  documentTitle: String(row.document_title || ''),
  documentKind: String(row.document_kind || ''),
  documentCreatedAt: String(row.document_created_at || ''),
  qty: Number(row.qty || 1),
  lineName: String(row.line_name || ''),
  lineDescription: String(row.line_description || ''),
  imageUrl: String(row.image_url || ''),
  internalFinish: String(row.internal_finish || 'Unknown'),
  externalFinish: String(row.external_finish || 'Unknown'),
  materialType: String(row.material_type || 'Unknown'),
  itemType: String(row.item_type || 'Unknown'),
  styleName: String(row.style_name || 'Unknown'),
  styleCategory: String(row.style_category || 'Unknown'),
  glassType: String(row.glass_type || 'Unknown'),
  profileType: String(row.profile_type || 'Unknown'),
  beadingType: String(row.beading_type || 'Unknown'),
  handleColor: String(row.handle_color || 'Unknown'),
  handleStyle: String(row.handle_style || 'Unknown'),
  hingeType: String(row.hinge_type || 'Unknown'),
  trickleVents: row.trickle_vents === true,
  widthMm: Number(row.width_mm || 0),
  heightMm: Number(row.height_mm || 0),
  openerCount: Number(row.opener_count || 0),
  isDoor: row.is_door === true,
  metaJson: row.meta_json && typeof row.meta_json === 'object' ? row.meta_json : {}
});

export async function searchFenestrationItems(
  filters: FenestrationFilters,
  page: number,
  pageSize: number
): Promise<{ items: FenestrationQueryItem[]; total: number }> {
  const { data, error } = await directSupabase.rpc(
    'search_fenestration_items',
    createFenestrationRpcParams(filters, page, pageSize)
  );
  if (error) throw new Error(error.message || 'Failed to search fenestration items');

  const rows = Array.isArray(data) ? data : [];
  return {
    items: rows.map(mapItem),
    total: Number(rows[0]?.total_count || 0)
  };
}

const sanitizeOptions = (value: any): FenestrationFilterOptions => {
  const normalize = (items: unknown): string[] =>
    Array.isArray(items) ? items.map(String).filter(Boolean) : [];

  return {
    internalFinishes: normalize(value?.internalFinishes),
    externalFinishes: normalize(value?.externalFinishes),
    materialTypes: normalize(value?.materialTypes),
    itemTypes: normalize(value?.itemTypes),
    styles: normalize(value?.styles),
    glassTypes: normalize(value?.glassTypes),
    profileTypes: normalize(value?.profileTypes),
    beadingTypes: normalize(value?.beadingTypes),
    handleColors: normalize(value?.handleColors),
    handleStyles: normalize(value?.handleStyles),
    hingeTypes: normalize(value?.hingeTypes),
    jointStyles: normalize(value?.jointStyles),
    cillOptions: normalize(value?.cillOptions),
    hingePlacements: normalize(value?.hingePlacements),
    openDirections: normalize(value?.openDirections),
    sources: normalize(value?.sources)
  };
};

export async function getFenestrationFilterOptions(): Promise<FenestrationFilterOptions> {
  if (typeof window !== 'undefined') {
    try {
      const cached = JSON.parse(sessionStorage.getItem(OPTIONS_CACHE_KEY) || 'null');
      if (cached?.storedAt && Date.now() - cached.storedAt < OPTIONS_CACHE_TTL_MS) {
        return sanitizeOptions(cached.options);
      }
    } catch {
      // Ignore malformed or unavailable browser storage.
    }
  }

  const { data, error } = await directSupabase.rpc('get_fenestration_filter_options');
  if (error) throw new Error(error.message || 'Failed to load fenestration filter options');
  const options = sanitizeOptions(data || EMPTY_OPTIONS);

  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(OPTIONS_CACHE_KEY, JSON.stringify({ storedAt: Date.now(), options }));
    } catch {
      // Storage is an optional optimization.
    }
  }
  return options;
}
